import { all, takeLatest, put, call } from 'redux-saga/effects';
import Api from 'src/helpers/api';
import { sendRequest } from 'src/helpers/saga';
import { logger } from 'src/helpers/logger';
import notificationsActions from 'src/redux/notifications/actions';
import { t } from 'src/i18n';
import { isSuccess } from '../rateEngineShared/apiStatus';
import actions from './actions';
import actionTypes from './actionTypes';
import parseErrors from './parseErrors';
import type { IRateEngineVersion, ISubmitRequestPayload } from './types';

const versionApi = new Api('pricing/engine_versions/:id');
const engineApi = new Api('pricing/engines');
const engineVersionsApi = new Api('pricing/engines/:id/engine_versions');

// `sendRequest` reports a validation failure as `{ status, validations }` only when asked to
// preserve the status; without it the caller cannot tell 422 from a transport failure.
const PRESERVE_STATUS = { preserveErrorStatus: true };

const HTTP_FORBIDDEN = 403;
const HTTP_NOT_FOUND = 404;

type LoadVersionAction = { type: typeof actionTypes.LOAD_VERSION_REQUEST; payload: { id: string } };
type ClearLoadedVersionAction = { type: typeof actionTypes.CLEAR_LOADED_VERSION };
type SubmitAction = { type: string; payload: ISubmitRequestPayload };

function genericErrorNotification() {
  return notificationsActions.addNotification({
    headline: t('dash.common.notification.error_headline'),
    type: 'error',
    message: t('dash.common.notification.something_went_wrong'),
  });
}

export function* loadVersion({ payload }: LoadVersionAction): Generator<any, void, any> {
  try {
    // No failure action is handed to `sendRequest`: its own dispatch carries no `requestedId`, so the reducer
    // reads it as an unrelated load and wipes an unsaved draft. Both exits below dispatch with the id.
    const result = yield call(sendRequest, versionApi.load, { id: payload.id });
    // A GET returns the raw response, so the payload sits under `success.data`.
    const version = result?.data?.success?.data as IRateEngineVersion | undefined;

    if (result?.status === 200 && version?.id) {
      yield put(actions.loadVersionSuccess(version));

      return;
    }

    yield put(actions.loadVersionError(t('dash.admin.rateEngine.editor.error.description'), payload.id));
  } catch (error) {
    logger.error(error);
    yield put(actions.loadVersionError(t('dash.admin.rateEngine.editor.error.description'), payload.id));
  }
}

// `takeLatest` also watches the clear so dropping a copy source cancels its in-flight load;
// otherwise a late success re-seeds the draft. The clear itself has no work to do.
export function* watchVersionSource(action: LoadVersionAction | ClearLoadedVersionAction): Generator<any, void, any> {
  if (action.type !== actionTypes.LOAD_VERSION_REQUEST) return;

  yield call(loadVersion, action);
}

function requestFor({ mode, engineId, versionId, body }: ISubmitRequestPayload) {
  if (mode === 'create') {
    return { request: engineApi.createItem, args: { data: body } };
  }

  if (mode === 'newVersion') {
    return { request: engineVersionsApi.createItem, args: { data: body, id: engineId } };
  }

  return { request: versionApi.patchItem, args: { data: body, id: versionId } };
}

export function* submit({ payload }: SubmitAction): Generator<any, void, any> {
  try {
    const { request, args } = requestFor(payload);
    const result = yield call(sendRequest, request, args, undefined, undefined, undefined, PRESERVE_STATUS);

    if (isSuccess(result)) {
      // A non-GET response is unwrapped to `{ data, status }`, so the version is `result.data`.
      const saved = result.data as IRateEngineVersion | undefined;

      if (saved?.id) {
        yield put(actions.submitSuccess(saved));

        return;
      }

      // The write went through but the body is unusable, so the draft cannot be re-seeded from it.
      // Saying so beats leaving the editor waiting for a version that will never arrive.
      logger.error(`[rateEngine] ${payload.mode} succeeded with an unreadable body`);
      yield put(actions.submitFailed({ base: [t('dash.admin.rateEngine.editor.errors.saved_but_unreadable')] }));

      return;
    }

    if (result?.validations) {
      yield put(actions.submitFailed(parseErrors(result.validations)));

      return;
    }

    // A 403 means the version is no longer a draft, so re-read it: the editor then shows the status
    // it actually has and drops its save control.
    if (result?.status === HTTP_FORBIDDEN) {
      const explanation = result.message ?? t('dash.admin.rateEngine.editor.errors.version_not_editable');

      yield put(actions.submitFailed({ base: [explanation] }));

      if (payload.versionId) yield put(actions.loadVersionRequest(payload.versionId));

      return;
    }

    // Only `edit` names a version; the other modes address a collection under an engine, so a 404
    // there means the engine is missing. Naming the wrong one sends the administrator hunting.
    if (result?.status === HTTP_NOT_FOUND) {
      const missing =
        payload.mode === 'edit'
          ? t('dash.admin.rateEngine.editor.errors.version_not_found')
          : t('dash.admin.rateEngine.editor.errors.engine_not_found');

      yield put(actions.submitFailed({ base: [missing] }));

      return;
    }

    yield put(actions.submitFailed());

    // A result at all means `sendRequest` returned rather than toasting; an undefined one was
    // already toasted, and two toasts for one failure is worse than none.
    if (result) yield put(genericErrorNotification());
  } catch (error) {
    logger.error(error);
    yield put(actions.submitFailed());
    yield put(genericErrorNotification());
  }
}

export default function* rootSaga() {
  yield all([
    takeLatest([actionTypes.LOAD_VERSION_REQUEST, actionTypes.CLEAR_LOADED_VERSION], watchVersionSource),
    takeLatest(actionTypes.SUBMIT_REQUEST, submit),
  ]);
}
